import { Component } from '@angular/core';
import { 
  SituAnalyticsWebApiClient, ListProductImpressionsGraphDataRequest, ProductImpressionGraphPoint, TimeScale, ProductName
} from 'situanalytics-webapi-client';
import { ListManufacturerProductsRequest, ManufacturerProduct, SituViewWebApiClient } from 'situview-webapi-client';
import { DateRangeTypes } from 'src/app/components/date-range/date-range.component';

import { ChartOptions, ChartType, ChartDataSets } from 'chart.js';
import { Label } from 'ng2-charts';
import { MatSelectChange } from '@angular/material/select';
import { MatDatepicker } from '@angular/material/datepicker';

import { FormControl } from '@angular/forms';
import { StateService } from 'src/app/services/state.service';
import * as Chart from 'chart.js';
import { AuthService, BreadcrumbService, CompanyService, ConfigService, IProductNameConfiguration, ProductNameService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

enum GraphType { Line = "Line", StackedL = "Stacked Line", Bar = "Bar", StackedB = "Stacked Bar" }

@Component({
  selector: 'view-product-impressions-graph',
  templateUrl: './product-impressions-graph.component.html',
  styleUrls: ['./product-impressions-graph.component.scss'],
})
export class ProductImpressionsGraphComponent extends SituBaseComponent {
  TimeScales = TimeScale;
  GraphTypes = GraphType;

  timeScaleMappings = ["Hour", "Day", "Month", "Auto"];
  avgtimeScaleMappings = ["Hourly", "Daily", "Monthly", "Auto"];

  parentFromDate: Date = new Date();
  parentToDate: Date = new Date();

  dateRange: DateRangeTypes = DateRangeTypes.Monthly;

  /* Deep linking */
  deepLinked: boolean = false;
  /* Deep linking */

  hourlyActive: boolean = false;
  dailyActive: boolean = false;
  monthlyActive: boolean = false;

  allManMessage = "All Manufacturers";
  allProdMessage = "All Products";
  displayTimeScale = "";

  // Params
  selectedTimeScale = TimeScale.Day;
  selectedGraphType = GraphType.Bar;
  selectedManu = this.allManMessage;
  selectedProduct = this.allProdMessage;  
  selectedSituProduct: IProductNameConfiguration | undefined = '' as any;
  isCumulative = false;
  showGoal = false;
  showPanoramaViews = false;

  manufacturers: string[] = [];
  products: ManufacturerProduct[] = [];
  displayProducts: any[] = [];  
  situProducts: IProductNameConfiguration[] | undefined;

  isGraphStacked = false;
  graphDisplayType: string = "bar";
  graphData = [];
  graphGoalPoints: number[] = [];
  graphViewPoints: number[] = [];

  // Helpful Facts
  totalImpressionCount = 0;
  billImpressionCount = 0;
  averageForTime = 0;
  averageTag = "";

  // Calculating the goal line.
  //The goal is calculated from a max $1000 worth of impressions per month, at $0.30 per impression
  impressionCountGoalMonthly = Math.ceil(1000 / .3);
  impressionCountGoalDaily = Math.ceil(this.impressionCountGoalMonthly / (365 / 12));
  impressionCountGoalHourly = Math.ceil(this.impressionCountGoalDaily / 12);
  impressionCountGoalYearly = this.impressionCountGoalMonthly * 12;

  serviceRequest: ListProductImpressionsGraphDataRequest | null = null;
  rawData: ProductImpressionGraphPoint[] = [];

  manuDropdownControl = new FormControl();
  prodDropdownControl = new FormControl();
  filteredManOptions: string[] = [];
  filteredProdOptions: string[] = [];
  useDarkTheme: boolean = false;

  /* Filters */
  rangeFilter: ProductImpressionGraphParams | undefined;
  /* Filters */

  /* Query Parameters */
  readonly RangeParamName: string = "range";
  readonly MonthParamName: string = "month";
  readonly CompanyParamName: string = "companyId";
  /* Query Parameters */

  // Store URL parameters for processing after auth is ready
  private urlCompanyParam: string | undefined;
  private urlMonthParam: string | undefined;

  // Date picker properties
  selectedDateTime: Date = new Date();
  selectedMonthDate: Date = new Date();
  minDate = new Date(2023, 10, 1); // Nov 1, 2023 (month is 0-indexed)
  maxDate = new Date(); // Current date

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private situViewClient: SituViewWebApiClient,
    private productNameService: ProductNameService,
    private breadcrumbService: BreadcrumbService,
    private route: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private snackBar: MatSnackBar,
    private companyService: CompanyService) {
      super(authService, stateService);

      if (this.route.snapshot.queryParams[this.RangeParamName]) {
        this.rangeFilter = this.decodeRange(this.route.snapshot.queryParams[this.RangeParamName]);
        this.deepLinked = true;
        this.isCumulative = this.rangeFilter.isCumulative;
      }

      // Store URL parameters for processing after auth is ready
      this.urlCompanyParam = this.route.snapshot.queryParams[this.CompanyParamName];
      this.urlMonthParam = this.route.snapshot.queryParams[this.MonthParamName];

      // Initialize monthly date picker with current month as default (will be overridden if URL param exists)
      this.initializeMonthlyDate();
  }

  ngOnInit() {
    super.ngOnInit();

    // Store the original company ID for URL parameter logic
    this.originalCompanyId = this.selectedCompany?.id;

    // Process URL parameters now that auth state is ready
    this.processUrlParameters();

    this.prodDropdownControl.setValue(this.allProdMessage);
    this.prodDropdownControl.disable();

    this.situProducts = this.productNameService.productNameConfigurations;
    this.getManufacturersAndProducts();
    
    // Set default time scale to Day for monthly view
    this.selectedTimeScale = TimeScale.Day;
    this.setTimeScale(this.selectedTimeScale);

    // Initialize monthly date range (may be overridden by URL month param)
    this.updateMonthlyDateRange();

    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      this.useDarkTheme = state.useDarkTheme;
      this.formatData(this.rawData);
    }));
    
    // Check if we came in via deep link and clean up URL after processing
    if (this.urlMonthParam || this.urlCompanyParam) {
      // Clear URL parameters after processing deep link
      setTimeout(() => {
        this.clearUrlParameters();
      }, 500);
      // Don't load data here - it will be loaded after URL parameters are processed
    } else {
      // Load initial data only if no URL parameters
      setTimeout(() => {
        this.getImpressionData();
      }, 100);
    }
  }

  protected onSelectedCompanyChange(): void {
    // Update URL parameters when company changes
    this.updateAllParams();
    
    // Load data for new company
    this.getImpressionData();
  }

  removeDarkMode() {
    throw new Error('Method not implemented.');
  }
  setDarkMode() {
    throw new Error('Method not implemented.');
  }

  filterManu(value: string) {
    const filterValue = value.toLowerCase();
    this.filteredManOptions = this.manufacturers.filter(option => option.toLowerCase().includes(filterValue));
  }

  filterProd(value: string) {
    let filterValue = value.toLowerCase();
    this.filteredProdOptions = this.displayProducts.filter(option => option.sku.toLowerCase().includes(filterValue));
  }

  restoreProdMessage() {
    this.prodDropdownControl.setValue(this.selectedProduct);
  }

  restoreManuMessage() {
    this.manuDropdownControl.setValue(this.selectedManu);
  }

  isTimeScaleDisabled(timeScale: TimeScale) {
    switch (timeScale) {
      case TimeScale.Day:
        return !this.dailyActive;
      case TimeScale.Hour:
        return !this.hourlyActive;
      case TimeScale.Month:
        return !this.monthlyActive;
      default:
        return true;
    }
  }

  getManufacturersAndProducts() {
    return this.situViewClient.listManufacturerProducts(new ListManufacturerProductsRequest({ correlationId: "7bda659c-9ad5-403c-9f86-ba6e289d1a8f" }))
      .then(response => {
        if (response.success && response.manufacturerProducts) {
          this.manufacturers = [...new Set(response.manufacturerProducts.map(x => x.manufacturer ?? ''))];
          this.manufacturers.sort();
          this.filteredManOptions = this.manufacturers;
          this.products = [...new Set(response.manufacturerProducts)]
        }
      }, error => {
      });
  }

  getProductsOfMan(man: string) {
    this.displayProducts = [];
    let tempList = [];

    this.products.forEach(x => {
      if (x.manufacturer == man) {
        tempList.push({ product: x.product, sku: x.displaySku, enabled: true });
      }
    });

    // Failsafe if there are no products for this manufacturer.
    if (tempList.length == 0) {
      tempList.push({ item: "No products to display", enabled: false });
    }

    this.displayProducts = tempList.sort();
    this.prodDropdownControl.setValue(this.allProdMessage);
    this.filteredProdOptions = this.displayProducts;
  }

  restrictByRange() {
    // For monthly view, always use daily time scale
    this.hourlyActive = false;
    this.dailyActive = true;
    this.monthlyActive = false;
    this.selectedTimeScale = TimeScale.Day;
    this.setTimeScale(TimeScale.Day);
  }

  // Filter functions.
  updateTo(to: Date | null) {
    if (to) {
      this.parentToDate = to;
      this.getImpressionData();
    }
  }

  updateFrom(from: Date | null) {
    if (from) {
      this.parentFromDate = from;
      this.getImpressionData();
    }
  }

  updateDateRange(range: DateRangeTypes) {
    this.dateRange = range;
    this.restrictByRange();
  }

  setTimeScale(scale: TimeScale) {
    this.selectedTimeScale = scale;
    this.displayTimeScale = this.timeScaleMappings[scale];
    this.averageTag = this.avgtimeScaleMappings[scale];
  }

  updateTimeScale(event: MatSelectChange) {
    let scale = event.value;
    this.setTimeScale(scale)
    this.getImpressionData();
  }

  setGraphType(event: MatSelectChange) {
    let graph = event.value;
    let selectedGraphType = graph;
    let graphDisplayType = graph.toLowerCase();
    let isGraphStacked = false;

    if (graph == GraphType.StackedB) {
      selectedGraphType = GraphType.StackedB;
      graphDisplayType = "bar";
      isGraphStacked = true;
    }

    if (graph == GraphType.StackedL) {
      selectedGraphType = GraphType.StackedL;
      graphDisplayType = "line";
      isGraphStacked = true;
    }

    this.selectedGraphType = selectedGraphType;
    this.graphDisplayType = graphDisplayType;
    this.isGraphStacked = isGraphStacked;

    this.getImpressionData();
  }

  setMan(event: string) {
    this.selectedManu = event;

    if (this.selectedManu == this.allManMessage) {
      this.prodDropdownControl.disable();
    } else {
      this.prodDropdownControl.enable();
    }

    this.setSelectedProd(this.allProdMessage);
    this.getProductsOfMan(this.selectedManu);
  }

  setSelectedProd(event: string) {
    this.selectedProduct = event;
    this.getImpressionData();
  }

  setSelectedSituProduct(event: MatSelectChange) {
    this.selectedSituProduct = event.value;
    this.getImpressionData();
  }

  
  toggleShowAllManufacturers() {
    this.showAllMan = !this.showAllMan;
    this.getImpressionData();
  }

  toggleShowGoal() {
    this.showGoal = !this.showGoal;
    this.getImpressionData();
  }

  toggleShowPanoramaViews() {
    this.showPanoramaViews = !this.showPanoramaViews;
    this.getImpressionData();
  }

  showAllMan: boolean = false;
  getImpressionData() {
    /* do the company check */
    if (this.rangeFilter?.companyId && this.rangeFilter?.companyId != this.selectedCompany?.id) {
      if (!this.authService.isAuthorizedForCompanyById(this.rangeFilter?.companyId)) {
        //this.showMessage(`Sorry! Your are not authorized for company ${this.rangeFilter?.companyId}.`);
        this.router.navigateByUrl('/unauthorized');
        return;
      }

      this.companyService.getCompanyById(this.rangeFilter?.companyId)
        .then((company) => {
          if (!company) return;

          this.stateService.state = {
            ...this.stateService.state,
            company
          };
        });
    }

    let from = this.parentFromDate;
    let dateTo = this.parentToDate;

    let man: string | undefined = this.selectedManu;
    let prod: string | undefined = this.selectedProduct;

    // if (this.selectedManu == this.allManMessage) {
    //   man = undefined;
    // }

    if (this.selectedProduct == this.allProdMessage) {
      prod = undefined;
    }

    let request = new ListProductImpressionsGraphDataRequest({
      dateTo: dateTo,
      dateFrom: from,
      //manufacturer: man,
      manufacturerCompanyId: this.showAllMan ? undefined : this.selectedCompany?.id,
      productSKU: prod,
      isCumulative: false, // Always false since we generate both views
      timeScale: this.selectedTimeScale,
      productName: this.selectedSituProduct?.productName as ProductName
    });

    // if (!!this.serviceRequest && this.serviceRequest.dateFrom == request.dateFrom && this.serviceRequest.dateTo == request.dateTo
    //   && this.serviceRequest.isCumulative == request.isCumulative /*&& this.serviceRequest.manufacturer == request.manufacturer*/
    //   && this.serviceRequest.productSKU == request.productSKU && this.serviceRequest.timeScale == request.timeScale
    //   && this.serviceRequest.productName == request.productName) {
    //   this.formatData(this.rawData);
    //   return;
    // }

    this.updateParams();

    this.serviceRequest = request;

    this.loading = true;
    this.situAnalyticsClient.listProductImpressionsGraphData(request)
      .then(response => {
        this.rawData = response.graphPoints ?? [];
        this.formatData(this.rawData);
        this.loading = false;
      }, error => {
        this.showMessage('Unable to load product impressions data. Please check your connection and try again.');
        this.rawData = [];
        this.formatData(this.rawData);
        this.loading = false;
      });
  }

  formatData(data: ProductImpressionGraphPoint[]) {
    this.barChartLabels = [];
    this.graphGoalPoints = [];

    let totalImpressionData: number[] = [];
    let billImpressionData: number[] = [];
    let cumulativeTotalData: number[] = [];
    let cumulativeBillableData: number[] = [];
    let filteredAverageSum = 0;

    let itterations = 0;
    let runningTotalSum = 0;
    let runningBillableSum = 0;

    this.totalImpressionCount = 0;
    this.billImpressionCount = 0;

    data.forEach(x => {
      if(!x.date){
        throw new Error("Date cannot be null");
      }
      let date = new Date(x.date.toString());

      // Always use daily format since we removed time scale
      this.barChartLabels.push(`${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`);

      this.totalImpressionCount += x.totalImpressionCount ?? 0;
      this.billImpressionCount += x.billableImpressionCount ?? 0;

      if (date < new Date()) {
        filteredAverageSum += x.totalImpressionCount ?? 0;
        itterations++;
      }

      // Regular data
      totalImpressionData.push(x.totalImpressionCount ?? 0);
      billImpressionData.push(x.billableImpressionCount ?? 0);

      // Cumulative data
      runningTotalSum += x.totalImpressionCount ?? 0;
      runningBillableSum += x.billableImpressionCount ?? 0;
      cumulativeTotalData.push(runningTotalSum);
      cumulativeBillableData.push(runningBillableSum);
    })

    this.averageForTime = Math.ceil(filteredAverageSum / itterations);

    let pointBorderColor = '';
    let totalCol = '';
    let billableCol = '';

    if (this.useDarkTheme) {
      Chart.defaults.global.defaultFontColor = '#BBB';
      pointBorderColor = '#000';
      totalCol = '#BBB';
      billableCol = '#EA157A';
    }
    else {
      Chart.defaults.global.defaultFontColor = '#444';
      pointBorderColor = '#FFF';
      totalCol = '#444';
      billableCol = '#EA157A';
    }

    // Regular chart datasets
    let totalRegular = {
      type: "bar", data: totalImpressionData, label: "Total Impressions",
      fill: false, order: 2, lineTension: 0,
      backgroundColor: totalCol,
      borderColor: totalCol,
      pointBackgroundColor: totalCol,
      pointBorderColor: pointBorderColor,
      hoverBackgroundColor: totalCol,
      hoverBorderColor: totalCol
    };

    let billableRegular = {
      type: "bar", data: billImpressionData, label: "Billable Impressions",
      fill: false, order: 1, lineTension: 0,
      backgroundColor: billableCol,
      borderColor: billableCol,
      pointBackgroundColor: billableCol,
      pointBorderColor: pointBorderColor,
      hoverBackgroundColor: billableCol,
      hoverBorderColor: billableCol,
    };

    // Cumulative chart datasets
    let totalCumulative = {
      type: "bar", data: cumulativeTotalData, label: "Total Impressions",
      fill: false, order: 2, lineTension: 0,
      backgroundColor: totalCol,
      borderColor: totalCol,
      pointBackgroundColor: totalCol,
      pointBorderColor: pointBorderColor,
      hoverBackgroundColor: totalCol,
      hoverBorderColor: totalCol
    };

    let billableCumulative = {
      type: "bar", data: cumulativeBillableData, label: "Billable Impressions",
      fill: false, order: 1, lineTension: 0,
      backgroundColor: billableCol,
      borderColor: billableCol,
      pointBackgroundColor: billableCol,
      pointBorderColor: pointBorderColor,
      hoverBackgroundColor: billableCol,
      hoverBorderColor: billableCol,
    };

    // Set chart data
    this.barChartData = [totalRegular, billableRegular];
    this.cumulativeChartData = [totalCumulative, billableCumulative];
  }

  buildGoalDataSet(numOfPoints: number) {
    let data = [];
    let goalNum = 0;
    // Determine the set based on the timespan

    switch (this.selectedTimeScale) {
      case TimeScale.Hour:
        goalNum = this.impressionCountGoalHourly;
        break;
      case TimeScale.Day:
        goalNum = this.impressionCountGoalDaily;
        break;
      case TimeScale.Month:
        goalNum = this.impressionCountGoalMonthly;
        break;
    }

    let cumulativeTotal = 0;

    // Build the line.
    for (let i = 0; i < numOfPoints; i++) {
      if (this.isCumulative) {
        cumulativeTotal += goalNum;
        data.push(cumulativeTotal);
      } else {
        data.push(goalNum);
      }
    }
    return data;
  }

  saveChartAsPNG() {
    // For now, just save the first (regular) chart
    // Could be enhanced to save both charts or let user choose
    let canvas = document.getElementById('canvas') as HTMLCanvasElement;
    if (canvas) {
      let url_base64 = canvas.toDataURL('image/png');
      let a = document.createElement('a');
      a.href = url_base64;
      a.download = "product_impression_chart.png";
      a.click();
    }
  }

  public barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0 // disables bezier curves
      }
    },
    scales: {
      yAxes: [{
        gridLines: {
          display: true,
          color: '#6666'
        },
        stacked: this.isGraphStacked,
        scaleLabel: {
          display: true,
          labelString: 'Number of Impressions'
        }
      }],
      xAxes: [{
        gridLines: {
          display: true,
          color: '#6666'
        },
        stacked: this.isGraphStacked,
        scaleLabel: {
          display: false,
          labelString: 'Time'
        }
      }]
    }
  };

  public chartColors = [];

  public barChartLabels: Label[] = [`No data loaded.`];
  public barChartType: ChartType = 'bar';
  public barChartLegend = true;

  public barChartData: ChartDataSets[] = [
    {
      type: "bar", data: [0], label: "No Data Found",
      backgroundColor: "#F3135F",
      fill: this.isGraphStacked,
      order: 0,
      lineTension: 0
    }
  ];

  public cumulativeChartData: ChartDataSets[] = [
    {
      type: "bar", data: [0], label: "No Data Found",
      backgroundColor: "#F3135F",
      fill: this.isGraphStacked,
      order: 0,
      lineTension: 0
    }
  ];

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    // if (this.rangeFilter !== undefined) {
    //   queryParams.range = this.rangeFilter;
    // }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    delete urlTree.queryParams[this.RangeParamName];
  
    this.router.navigateByUrl(urlTree);
  }

  private encodeRange(rangeParams: ProductImpressionGraphParams): string {
    const jsonString = JSON.stringify(rangeParams);
    return btoa(jsonString);
  }

  private decodeRange(encodedString: string): ProductImpressionGraphParams {
    try {
      const jsonString = atob(encodedString);
      return JSON.parse(jsonString) as ProductImpressionGraphParams;
    } catch (error) {
      return new ProductImpressionGraphParams(this.selectedCompany!.id!, DateRangeTypes.Monthly, new Date(), false);
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  // Monthly date picker methods
  initializeMonthlyDate(): void {
    const nowUtc = new Date();
    
    // Default to previous month UTC for better data availability (like Platform Usage)
    const previousMonthUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(), 
      nowUtc.getUTCMonth() - 1, 
      1
    ));
    this.selectedMonthDate = previousMonthUtc;
    this.selectedDateTime = previousMonthUtc;
    
    // Update max date to current month minus 1 day to prevent future dates
    const maxDateUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(), 
      nowUtc.getUTCMonth(), 
      nowUtc.getUTCDate() - 1
    ));
    this.maxDate = maxDateUtc;
    
    // Initialize the date range immediately
    this.updateMonthlyDateRange();
  }

  setMonthAndYear(normalizedMonthAndYear: Date, datepicker: MatDatepicker<Date>) {
    // Set month in UTC
    this.selectedMonthDate = new Date(Date.UTC(
      normalizedMonthAndYear.getFullYear(), 
      normalizedMonthAndYear.getMonth(), 
      1, 0, 0, 0, 0
    ));
    
    this.hasUserNavigatedMonth = true; // User has manually navigated
    datepicker.close();
    this.updateMonthlyDateRange();
    this.updateAllParams(); // Update URL parameters
    this.getImpressionData();
  }

  onDateTimeChange(selectedDate: Date) {
    if (selectedDate) {
      this.selectedMonthDate = selectedDate;
      this.hasUserNavigatedMonth = true;
      this.updateMonthlyDateRange();
      this.updateAllParams();
      this.getImpressionData();
    }
  }

  private updateMonthlyDateRange() {
    // Set date range to selected month
    this.parentFromDate = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(), 
      this.selectedMonthDate.getUTCMonth(), 
      1, 0, 0, 0, 0
    ));
    this.parentToDate = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(), 
      this.selectedMonthDate.getUTCMonth() + 1, 
      0, 23, 59, 59, 999
    ));
    this.dateRange = DateRangeTypes.Monthly;
    
    // Update time scale restrictions
    this.restrictByRange();
  }

  getTimeScaleLabel(): string {
    if (this.selectedTimeScale === undefined || this.selectedTimeScale === null) {
      return 'Auto';
    }
    
    // Map TimeScale enum values to readable strings
    const timeScaleMap: { [key: number]: string } = {
      [TimeScale.Hour]: 'Hourly',
      [TimeScale.Day]: 'Daily', 
      [TimeScale.Month]: 'Monthly',
      [TimeScale.Auto]: 'Auto'
    };
    
    return timeScaleMap[this.selectedTimeScale] || 'Auto';
  }

  getDisplayMonthYear(): string {
    if (!this.selectedMonthDate) {
      return '';
    }
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const month = monthNames[this.selectedMonthDate.getUTCMonth()];
    const year = this.selectedMonthDate.getUTCFullYear();
    
    return `${month} ${year}`;
  }

  // No longer automatically updating URL parameters
  private updateAllParams() {
    // Method kept for compatibility but no longer modifies URL
    // URL parameters are only used for incoming deep links
  }

  // Clear URL parameters to keep URL clean
  private clearUrlParameters() {
    const urlTree = this.router.createUrlTree([], {
      queryParams: {},
      preserveFragment: false
    });
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private originalCompanyId: string | undefined;
  private hasUserNavigatedMonth: boolean = false;

  private getOriginalCompanyId(): string | undefined {
    // Return the company ID that was active when the component first loaded
    return this.originalCompanyId;
  }

  private formatMonthForUrl(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  private setMonthFromUrlParam(monthParam: string) {
    try {
      const [year, month] = monthParam.split('-').map(Number);
      if (year && month && month >= 1 && month <= 12) {
        this.selectedMonthDate = new Date(Date.UTC(year, month - 1, 1));
        this.updateMonthlyDateRange();
        return;
      }
    } catch (error) {
      // Invalid month parameter, fall back to default
    }
    
    // Fallback to default if parameter is invalid
    this.initializeMonthlyDate();
  }

  private processUrlParameters() {
    // Wait for auth service to be ready before processing company parameters
    const checkAuthReady = () => {
      // Check if user is logged in (auth is ready)
      if (this.authService.isLoggedIn()) {
        // Process company parameter if provided
        if (this.urlCompanyParam) {
          this.setCompanyFromUrlParam(this.urlCompanyParam);
        }

        // Process month parameter if provided
        if (this.urlMonthParam) {
          this.setMonthFromUrlParam(this.urlMonthParam);
        }
      } else {
        // If auth not ready, wait a bit longer
        setTimeout(checkAuthReady, 200);
      }
    };

    // Start checking after initial delay
    setTimeout(checkAuthReady, 100);
  }

  private setCompanyFromUrlParam(companyParam: string) {
    // Check if user is authorized for this company
    if (!this.authService.isAuthorizedForCompanyById(companyParam)) {
      this.showMessage('You are not authorized to view data for the requested company.');
      // Don't redirect immediately - just show message and continue with current company
      return;
    }

    // Set the company in the state service when coming in via URL
    this.companyService.getCompanyById(companyParam)
      .then((company) => {
        if (company) {
          // Update the state with the company from URL
          this.stateService.state = {
            ...this.stateService.state,
            company
          };
          // Trigger data reload with new company
          setTimeout(() => {
            this.getImpressionData();
          }, 100);
        } else {
          this.showMessage('The requested company could not be found. Showing current company data.');
        }
      })
      .catch((error) => {
        this.showMessage('Error loading company data. Showing current company data.');
      });
  }

  // Copy shareable URL to clipboard
  copyShareableUrl() {
    const shareableUrl = this.generateShareableUrl();
    
    if (navigator.clipboard && window.isSecureContext) {
      // Modern approach using Clipboard API
      navigator.clipboard.writeText(shareableUrl).then(() => {
        this.showMessage('Shareable link copied to clipboard!');
      }).catch(err => {
        this.fallbackCopyTextToClipboard(shareableUrl);
      });
    } else {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(shareableUrl);
    }
  }

  private fallbackCopyTextToClipboard(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showMessage('Shareable link copied to clipboard!');
      } else {
        this.showMessage('Unable to copy link. Please copy manually from address bar.');
      }
    } catch (err) {
      this.showMessage('Unable to copy link. Please copy manually from address bar.');
    }
    
    document.body.removeChild(textArea);
  }

  // Helper method to generate shareable URLs for monthly reports
  // Handle datetime picker changes
  onDateTimeChanged(dateTime: Date | null): void {
    if (dateTime) {
      this.selectedDateTime = dateTime;
      this.selectedMonthDate = new Date(dateTime);
      this.updateMonthlyDateRange();
      this.getImpressionData();
    }
  }

  private generateShareableUrl(month?: Date, companyId?: string): string {
    const baseUrl = window.location.origin + this.router.url.split('?')[0];
    const params = new URLSearchParams();
    
    // Always add current company ID for sharing
    const targetCompanyId = companyId || this.selectedCompany?.id;
    if (targetCompanyId) {
      params.set(this.CompanyParamName, targetCompanyId);
    }
    
    // Always add month parameter for exact sharing
    const targetMonth = month || this.selectedMonthDate;
    const targetMonthKey = this.formatMonthForUrl(targetMonth);
    params.set(this.MonthParamName, targetMonthKey);
    
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  // Month navigation methods
  navigateToPreviousMonth() {
    if (this.isPreviousMonthDisabled()) {
      return;
    }
    
    const prevMonth = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() - 1,
      1
    ));
    
    this.selectedMonthDate = prevMonth;
    this.hasUserNavigatedMonth = true; // User has manually navigated
    this.updateMonthlyDateRange();
    this.updateAllParams();
    this.getImpressionData();
  }

  navigateToNextMonth() {
    if (this.isNextMonthDisabled()) {
      return;
    }
    
    const nextMonth = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() + 1,
      1
    ));
    
    this.selectedMonthDate = nextMonth;
    this.hasUserNavigatedMonth = true; // User has manually navigated
    this.updateMonthlyDateRange();
    this.updateAllParams();
    this.getImpressionData();
  }

  isPreviousMonthDisabled(): boolean {
    if (!this.selectedMonthDate || !this.minDate) {
      return true;
    }
    
    const prevMonth = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() - 1,
      1
    ));
    
    const minMonth = new Date(Date.UTC(
      this.minDate.getUTCFullYear(),
      this.minDate.getUTCMonth(),
      1
    ));
    
    return prevMonth < minMonth;
  }

  isNextMonthDisabled(): boolean {
    if (!this.selectedMonthDate || !this.maxDate) {
      return true;
    }
    
    const nextMonth = new Date(Date.UTC(
      this.selectedMonthDate.getUTCFullYear(),
      this.selectedMonthDate.getUTCMonth() + 1,
      1
    ));
    
    const maxMonth = new Date(Date.UTC(
      this.maxDate.getUTCFullYear(),
      this.maxDate.getUTCMonth(),
      1
    ));
    
    return nextMonth > maxMonth;
  }
}

export class ProductImpressionGraphParams {
  constructor(public companyId: string, public dateRangeType: DateRangeTypes, public date: Date, public isCumulative: boolean) { }
}